import { connect, FormikValues } from 'formik'
import { flatten, get } from 'lodash'
import { Fragment, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Divider } from 'semantic-ui-react'
import styled from 'styled-components'
import { v4 } from 'uuid'

import { Text } from '@/components/Form'
import { MentionsRichTextEditorWithFormik } from '@/components/Form/MentionsRichTextEditor/MentionsRichTextEditor'
import { AddCircle, Delete } from '@/components/Icons'
import { FormLabel, Row } from '@/components/StyledComponents'
import Button from '@/components/TailwindUI/Button'
import {
  StyledWandIcon,
  VariableButton,
  VariableInputContainer
} from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableOverlay from '@/pages/SpaceAppBuilder/components/VariableInput/VariableOverlay'
import { APIRequestInputQuery, FieldTypes, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'

import { AddRuleProps } from './Boolean/Conditions/interfaces'
import messages from './messages'
import operationsMessages from './messages'

const StyledContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 10px;
`

const StyledTitle = styled.p`
  &.data-source-operation {
    font-size: 0.9rem;
  }
  font-weight: bold;
  font-size: 1.1rem;
  margin-top: 0.5rem;
  margin-bottom: 0.6rem;
`

const StyledMentionsRichTextEditorWithFormik = styled(MentionsRichTextEditorWithFormik)`
  max-width: ${props => (props.mentions?.length > 0 ? '500px' : '100%')};
`

const DeleteButton = styled.div`
  margin-left: auto;
  cursor: pointer;
  min-width: 0;
  &:hover {
    g {
      fill: #db3939;
    }
  }
`

const StyledTextInput = styled(Text)`
  width: 100%;
`

//<editor-fold desc="Styled Components">
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`

const StyledSpan = styled.span`
  color: #ff0000;
  margin-right: 5px;
`
//</editor-fold>

const StyledDivider = styled(Divider)`
  margin-bottom: 5px !important;
  margin-top: 5px !important;
`

interface QueryParametersProps {
  readOnly?: boolean
  variables: Variable[][]
  queryParamPath: string
  isQueryParamRequired?: boolean
  operation?: string
}

const QueryParameters = ({
  formik,
  readOnly,
  operation,
  variables,
  queryParamPath,
  isQueryParamRequired = false
}: QueryParametersProps & FormikValues) => {
  const intl = useIntl()

  const queries = get(formik, `values.${queryParamPath}`, [])
  const addVariableRef = useRef<any>([])
  const [showVariablePopOver, setShowVariablePopOver] = useState<string[]>([])

  useEffect(() => {
    if (isQueryParamRequired && (!queries || queries.length === 0)) {
      formik.setFieldValue(`${queryParamPath}[${queries.length}.id`, v4())
      formik.setFieldValue(`${queryParamPath}[${queries.length}.key`, '')
      formik.setFieldValue(`${queryParamPath}[${queries.length}.value`, '')
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isQueryParamRequired, formik.values])

  const onVariablePopOverClick = (variablePopoverKey: string) => {
    setShowVariablePopOver((prev: string[]) => {
      if (prev.includes(variablePopoverKey)) {
        return prev.filter(accordionkey => accordionkey !== variablePopoverKey)
      } else {
        return prev.concat(variablePopoverKey)
      }
    })
  }

  const AddQuery = ({ onClick }: AddRuleProps) => {
    return (
      <Button onClick={onClick} color="transparent" className="flex items-center space-x-2 text-blue-600">
        <AddCircle />
        <FormattedMessage {...messages.addQuery} />
      </Button>
    )
  }

  const getQueryValuePath = (index: number) => {
    return `${queryParamPath}[${index}].value`
  }

  return (
    <StyledContainer>
      <StyledTitle className={operation || ''}> {intl.formatMessage(messages.queryParameter)} </StyledTitle>
      {queries.map((query: APIRequestInputQuery, index: number) => {
        return (
          <Fragment key={`query-${query.id}`}>
            <StyledRow>
              <FormLabel>
                <StyledSpan>{intl.formatMessage(messages.requiredStar)}</StyledSpan>
                <FormattedMessage {...messages.queryTitle} values={{ index: index + 1 }} />
              </FormLabel>
              <DeleteButton
                onClick={() => {
                  formik.setFieldValue(
                    queryParamPath,
                    queries.filter((q: any) => q.id !== query.id)
                  )
                }}
              >
                <Delete className="flex-center" />
              </DeleteButton>
            </StyledRow>
            <StyledTextInput
              name={`${queryParamPath}[${index}].key`}
              disabled={readOnly}
              required={true}
              placeholder={intl.formatMessage(messages.enterQueryName)}
              onChange={() => {}}
              variant={undefined}
              className={''}
            />
            <VariableInputContainer>
              <Row>
                <StyledMentionsRichTextEditorWithFormik
                  readOnly={readOnly}
                  required={true}
                  placeholder={intl.formatMessage(messages.selectQuery)}
                  setAddMention={addMentionFn => (addVariableRef.current[getQueryValuePath(index)] = addMentionFn)}
                  name={getQueryValuePath(index)}
                  formik={formik}
                  multiLine={false}
                  mentions={getAllFlattenedVariables(flatten(variables))}
                />
                {variables.length > 0 && !readOnly && (
                  <VariableButton
                    onClick={() => {
                      onVariablePopOverClick(getQueryValuePath(index))
                    }}
                  >
                    <StyledWandIcon title={intl.formatMessage(operationsMessages.addVariableButton)} />
                  </VariableButton>
                )}
              </Row>
              {showVariablePopOver.includes(getQueryValuePath(index)) && (
                <VariableOverlay
                  onSelectVariable={(variable: Variable) => addVariableRef.current[getQueryValuePath(index)](variable)}
                  onClose={() =>
                    setShowVariablePopOver((prev: string[]) => {
                      return prev.filter(accordionkey => accordionkey !== getQueryValuePath(index))
                    })
                  }
                  variables={variables}
                  validVariableTypes={[
                    FieldTypes.STRING,
                    FieldTypes.BOOLEAN,
                    FieldTypes.INTEGER,
                    FieldTypes.NUMBER,
                    FieldTypes.ANY
                  ]}
                />
              )}
              <StyledDivider section />
            </VariableInputContainer>
          </Fragment>
        )
      })}
      {!readOnly && (
        <AddQuery
          onClick={() => {
            formik.setFieldValue(`${queryParamPath}[${queries.length}.id`, v4())
            formik.setFieldValue(`${queryParamPath}[${queries.length}.key`, '')
            formik.setFieldValue(`${queryParamPath}[${queries.length}.value`, '')
          }}
        />
      )}
    </StyledContainer>
  )
}

export default connect(QueryParameters)
